import { useEffect, useState } from "react";
import { LayoutGrid, Minus, Plus, Search } from "lucide-react";
import { api, money } from "@/lib/api";
import { ItemThumb } from "@/components/ItemImage";
import { calcTotals } from "@/components/QuoteLines";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const toLine = (it) => ({ item_id: it.id, sku: it.sku, name: it.name, description: it.description || "", qty: 1, unit_price: it.unit_price, cost_price: it.cost_price || 0, has_gst: it.has_gst, stock: it.stock });

const Tile = ({ it, qty, onChange }) => (
  <div className={`relative flex flex-col overflow-hidden rounded-lg border bg-white transition-shadow ${qty ? "border-[#1BA3E0] ring-2 ring-[#1BA3E0]/30" : "border-slate-200"}`} data-testid={`grid-pick-${it.sku}`}>
    <span className="num absolute left-2 top-2 z-10 rounded bg-[#1BA3E0] px-2 py-0.5 text-xs font-semibold text-white">{money(it.unit_price)}</span>
    {qty > 0 && <span className="num absolute right-2 top-2 z-10 grid h-7 min-w-7 place-items-center rounded-full bg-[#01195D] px-2 text-xs font-bold text-white" data-testid={`grid-pick-qty-${it.sku}`}>{qty}</span>}
    <button type="button" className="aspect-square p-3" onClick={() => onChange(it, 1)}><ItemThumb item={it} className="h-full w-full" /></button>
    <div className="flex-1 px-3 text-sm font-semibold leading-snug text-[#0B1523]">{it.name}</div>
    <div className="flex items-center justify-between px-2 py-1">
      <Button size="icon" variant="ghost" disabled={!qty} onClick={() => onChange(it, -1)} className="text-red-500" data-testid={`grid-pick-minus-${it.sku}`}><Minus className="h-5 w-5" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onChange(it, 1)} className="text-[#1BA3E0]" data-testid={`grid-pick-plus-${it.sku}`}><Plus className="h-5 w-5" /></Button>
    </div>
    <div className={`px-3 py-1 text-xs font-semibold ${it.stock > 0 ? "bg-slate-100 text-slate-700" : "bg-red-50 text-red-700"}`}>{it.stock} in stock</div>
  </div>
);

export const ItemGridPicker = ({ lines, setLines }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/items", { params: { q } }).then((r) => setRows(r.data)), 200);
    return () => clearTimeout(t);
  }, [open, q]);

  const qtyOf = (it) => Number(lines.find((l) => l.item_id === it.id)?.qty || 0);
  const change = (it, d) => {
    const i = lines.findIndex((l) => l.item_id === it.id);
    if (i === -1) return d > 0 && setLines([...lines, toLine(it)]);
    const n = Number(lines[i].qty || 0) + d;
    setLines(n <= 0 ? lines.filter((_, j) => j !== i) : lines.map((l, j) => (j === i ? { ...l, qty: n } : l)));
  };

  const cats = [...new Set(rows.map((r) => r.category || "Other"))].sort();
  const shown = cat ? rows.filter((r) => (r.category || "Other") === cat) : rows;
  const t = calcTotals(lines);

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="quote-grid-picker-btn"><LayoutGrid className="mr-1 h-4 w-4" /> Browse items</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[90vh] max-w-6xl flex-col gap-0 p-0" data-testid="quote-grid-picker">
          <DialogHeader className="space-y-3 border-b border-slate-200 p-5">
            <DialogTitle className="text-[#01195D]">Add items to quote</DialogTitle>
            <DialogDescription>Tap + or – on each item. Tap the picture to add one.</DialogDescription>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search name or code" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="grid-pick-search" />
            </div>
            {cats.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {["", ...cats].map((c) => (
                  <button key={c || "all"} onClick={() => setCat(c)} data-testid={`grid-pick-cat-${c || "all"}`}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${cat === c ? "border-[#1BA3E0] bg-[#1BA3E0] text-white" : "border-[#1BA3E0]/40 text-[#1BA3E0]"}`}>{c || "All"}</button>
                ))}
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-slate-50 p-5">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {shown.map((it) => <Tile key={it.id} it={it} qty={qtyOf(it)} onChange={change} />)}
            </div>
            {!shown.length && <p className="py-16 text-center text-slate-500">No items found.</p>}
          </div>
          <div className="flex items-center justify-between bg-[#01195D] px-5 py-3 text-white">
            <span className="text-sm">{lines.length} line{lines.length === 1 ? "" : "s"} · Total ex. tax <span className="num ml-2 text-lg font-bold" data-testid="grid-pick-total">{money(t.sub)}</span></span>
            <Button onClick={() => setOpen(false)} className="bg-[#1BA3E0] hover:bg-[#1590C8]" data-testid="grid-pick-done-btn">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
